import theme from 'config/theme';
import {UploadsBar} from 'interface/stacks/uploads/views/UploadsBar';
import {UploadsItem} from 'interface/stacks/uploads/views/UploadsItem';
import {useCallback, useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {
  DataProvider,
  LayoutProvider,
  RecyclerListView,
} from 'recyclerlistview/web';

import type {UploaderState} from 'interface/stacks/uploads/hooks/useUploader';

const UPLOADER_SIZE = {
  width: 320,
  height: 320,
  right: 10,
  bottom: 10,
};

const ITEM_HEIGHT = 60; // Approximate height of each upload item

export function Uploads(uploader: UploaderState) {
  const [maximized, setMaximized] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const members = [];

  // Create data provider
  const dataProvider = useMemo(
    () =>
      new DataProvider(
        (r1, r2) => r1.metadata.uuid !== r2.metadata.uuid,
      ).cloneWithRows(Array.from(uploader.uploads)),
    [uploader.uploads],
  );

  // Create layout provider
  const layoutProvider = useMemo(
    () =>
      new LayoutProvider(
        () => 'upload_item',
        (type, dim) => {
          dim.width = UPLOADER_SIZE.width;
          dim.height = ITEM_HEIGHT;
        },
      ),
    [],
  );

  // Calculate list height based on number of items
  const listHeight = useMemo(() => {
    const itemCount = uploader.uploads.length;
    const calculatedHeight = itemCount * ITEM_HEIGHT;
    return Math.min(calculatedHeight, UPLOADER_SIZE.height - 40);
  }, [uploader.uploads.length]);

  // Extended state for RLV
  const extendedState = useMemo(
    () => ({
      uploader,
      members,
    }),
    [uploader, members],
  );

  // Row renderer
  const rowRenderer = useCallback(
    (type: string, item: any, index: number, ext: typeof extendedState) => {
      return (
        <UploadsItem
          key={item.metadata.uuid}
          {...{
            uploader: ext.uploader,
            item,
            member: ext.members?.find(m => m.id === item.memberId),
          }}
        />
      );
    },
    [],
  );

  if (uploader.uploads.length === 0) return null;

  return (
    <View style={styles.root}>
      <UploadsBar
        {...uploader}
        {...{maximized, showAll, setMaximized, setShowAll}}
      />
      {maximized && (
        <View style={[styles.listContainer, {height: listHeight}]}>
          <RecyclerListView
            layoutProvider={layoutProvider}
            dataProvider={dataProvider}
            rowRenderer={rowRenderer}
            renderAheadOffset={screen.height / 2}
            scrollThrottle={16}
            style={styles.list}
            extendedState={extendedState}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    left: '50%',
    width: UPLOADER_SIZE.width,
    transform: [{translateX: -UPLOADER_SIZE.width / 2}],
    maxHeight: UPLOADER_SIZE.height,
    bottom: UPLOADER_SIZE.bottom,
    right: UPLOADER_SIZE.right,
    boxShadow:
      '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 6px 12px 0px rgba(0, 0, 0, 0.07)',
    backgroundColor: theme.colors.neutral.$1,
    borderRadius: 7,
    overflow: 'hidden',
  },
  listContainer: {
    width: UPLOADER_SIZE.width,
  },
  list: {
    height: '100%',
    width: '100%',
    scrollbarWidth: 'none',
  },
});
